import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_IDLE_MS, clampWhenHidden } from "@/lib/realtime";
import { reconcilePlan } from "@/app/routes/dashboard/content-calendar/api/calendarCache";
import type { ProductionBriefBody } from "@/types/productionBrief.type";
import type {
  WorkshopIdea,
  WorkshopIdeaBrief,
  WorkshopIdeaListItem,
  WorkshopIdeaMigration,
} from "@/types/workshop.type";
import {
  workshopIdeaKey,
  workshopIdeasKey,
  workshopIdeasListKey,
} from "./workshopKeys";

/** Mirrors the module-private `plansKey` in content-calendar/api/useContentCalendar. */
const calendarPlansKey = (brandId: string | null) => ["calendarPlans", brandId];

// An idea's reference video is presigned per response — refetching mints a new
// URL and restarts playback. Archive/migrate, brief edits and the chat's idea and
// brief tools invalidate this key when it actually changes, so no background
// polling, and staleTime keeps the global focus-refetch off it too.
const IDEA_STALE_MS = 5 * 60_000;

/** One project's idea list, as a reusable read (see workshopArtifactsQuery). */
export function workshopIdeasQuery(projectId: string | null, includeArchived: boolean) {
  return queryOptions({
    queryKey: workshopIdeasListKey(projectId, includeArchived),
    queryFn: async () => {
      const res = await axiosInstance.get<{ ideas: WorkshopIdeaListItem[] }>(
        `/workshop/projects/${projectId}/ideas?includeArchived=${includeArchived}`,
      );
      return res.data.ideas;
    },
    enabled: !!projectId,
  });
}

export function useWorkshopIdeas(
  projectId: string | null,
  includeArchived: boolean,
) {
  return useQuery({
    ...workshopIdeasQuery(projectId, includeArchived),
    // Chat turns write ideas on their own schedule — idle floor.
    refetchInterval: () => clampWhenHidden(POLL_IDLE_MS),
  });
}

export function useWorkshopIdea(ideaId: string | null) {
  return useQuery({
    queryKey: workshopIdeaKey(ideaId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ idea: WorkshopIdea }>(
        `/workshop/ideas/${ideaId}`,
      );
      return res.data.idea;
    },
    enabled: !!ideaId,
    staleTime: IDEA_STALE_MS,
  });
}

export function useWorkshopIdeaMutations(
  projectId: string | null,
  brandId: string | null,
) {
  const qc = useQueryClient();

  const invalidateIdea = (ideaId: string) => {
    void qc.invalidateQueries({ queryKey: workshopIdeasKey(projectId) });
    void qc.invalidateQueries({ queryKey: workshopIdeaKey(ideaId) });
  };

  /**
   * Archive or restore, optimistic like the artifacts' (useWorkshopArtifacts):
   * the row moves at once and only this row is put back on a refusal.
   */
  const archiveMutationKey = ["workshopIdeaArchive", projectId];
  const archiveIdea = useMutation({
    mutationKey: archiveMutationKey,
    mutationFn: async (vars: { ideaId: string; archived: boolean }) => {
      const res = await axiosInstance.post<{ idea: WorkshopIdea }>(
        `/workshop/ideas/${vars.ideaId}/archive`,
        { archived: vars.archived },
      );
      return res.data.idea;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: workshopIdeasKey(projectId) });
      const activeKey = workshopIdeasListKey(projectId, false);
      const allKey = workshopIdeasListKey(projectId, true);
      const row =
        qc.getQueryData<WorkshopIdeaListItem[]>(allKey)?.find((i) => i.id === vars.ideaId) ??
        qc.getQueryData<WorkshopIdeaListItem[]>(activeKey)?.find((i) => i.id === vars.ideaId) ??
        null;
      // A restored idea that went to the calendar is MIGRATED again; the server confirms.
      const statusFor = (migratedAt: string | null | undefined) =>
        vars.archived ? "ARCHIVED" : migratedAt ? "MIGRATED" : "ACTIVE";
      qc.setQueryData<WorkshopIdeaListItem[]>(allKey, (list) =>
        list?.map((i) => (i.id === vars.ideaId ? { ...i, status: statusFor(i.migratedAt) } : i)),
      );
      qc.setQueryData<WorkshopIdeaListItem[]>(activeKey, (list) => {
        if (!list) return list;
        if (vars.archived) return list.filter((i) => i.id !== vars.ideaId);
        if (!row || list.some((i) => i.id === row.id)) return list;
        return [{ ...row, status: statusFor(row.migratedAt) }, ...list];
      });
      qc.setQueryData<WorkshopIdea>(workshopIdeaKey(vars.ideaId), (idea) =>
        idea ? { ...idea, status: statusFor(idea.migratedAt) } : idea,
      );
      return { row };
    },
    onError: (_error, vars, context) => {
      const row = context?.row;
      if (row) {
        qc.setQueryData<WorkshopIdeaListItem[]>(workshopIdeasListKey(projectId, true), (list) =>
          list?.map((i) => (i.id === row.id ? row : i)),
        );
        qc.setQueryData<WorkshopIdeaListItem[]>(workshopIdeasListKey(projectId, false), (list) => {
          if (!list) return list;
          const without = list.filter((i) => i.id !== row.id);
          return row.status === "ARCHIVED" ? without : [row, ...without];
        });
      }
      void qc.invalidateQueries({ queryKey: workshopIdeaKey(vars.ideaId) });
    },
    // Reconcile once the last archive or restore has settled (see the artifacts').
    onSettled: (_data, _error, vars) => {
      void qc.invalidateQueries({ queryKey: workshopIdeaKey(vars.ideaId) });
      if (qc.isMutating({ mutationKey: archiveMutationKey }) > 1) return;
      invalidateIdea(vars.ideaId);
    },
  });

  const migrateIdea = useMutation({
    mutationFn: async (vars: { ideaId: string; date: string }) => {
      const res = await axiosInstance.post<WorkshopIdeaMigration>(
        `/workshop/ideas/${vars.ideaId}/migrate`,
        { date: vars.date },
      );
      return res.data;
    },
    onSuccess: (result) => {
      invalidateIdea(result.idea.id);
      // The month may have been materialized by this very call, so the plans
      // list matters as much as the plan itself.
      reconcilePlan(qc, result.planId);
      void qc.invalidateQueries({ queryKey: calendarPlansKey(brandId) });
    },
  });

  /**
   * A hand edit of the idea's brief, optimistic: the new body shows as the next
   * version at once. `baseVersion` is the server's compare-and-swap — the chat
   * rewrites briefs too, and a save written against a version it already
   * replaced is refused rather than silently clobbering it.
   *
   * Rollback touches only the brief, so an idea refetch that landed in between
   * (a chat turn renaming it, say) is not undone with it.
   */
  const editBrief = useMutation({
    mutationFn: async (vars: {
      ideaId: string;
      baseVersion: number;
      body: ProductionBriefBody;
      changeNote?: string;
    }) => {
      const res = await axiosInstance.patch<{ brief: WorkshopIdeaBrief }>(
        `/workshop/ideas/${vars.ideaId}/brief`,
        { baseVersion: vars.baseVersion, body: vars.body, changeNote: vars.changeNote },
      );
      return res.data.brief;
    },
    onMutate: async (vars) => {
      const key = workshopIdeaKey(vars.ideaId);
      await qc.cancelQueries({ queryKey: key });
      const previousBrief = qc.getQueryData<WorkshopIdea>(key)?.brief ?? null;
      qc.setQueryData<WorkshopIdea>(key, (current) => {
        if (!current?.brief) return current;
        const version = vars.baseVersion + 1;
        const live = current.brief.revisions[0];
        return {
          ...current,
          brief: {
            ...current.brief,
            version,
            revisions: live
              ? [
                  {
                    ...live,
                    version,
                    body: vars.body,
                    changeNote: vars.changeNote ?? null,
                    authorType: "TEAM",
                  },
                ]
              : current.brief.revisions,
          },
        };
      });
      return { previousBrief };
    },
    onError: (_error, vars, context) => {
      qc.setQueryData<WorkshopIdea>(workshopIdeaKey(vars.ideaId), (current) =>
        current && context ? { ...current, brief: context.previousBrief } : current,
      );
    },
    onSuccess: (brief, vars) => {
      // The server's copy carries freshly resolved shot visuals.
      qc.setQueryData<WorkshopIdea>(workshopIdeaKey(vars.ideaId), (current) =>
        current ? { ...current, brief } : current,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: workshopIdeasKey(projectId) });
    },
  });

  return { archiveIdea, migrateIdea, editBrief };
}
